import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Skeleton } from '@/components/ui';
import { actAs } from '@/lib/api';
import { useSession, type Me } from '@/lib/auth';
import { LoginPage } from '@/features/auth/LoginPage';
import { Shell } from './Shell';
import { AdminDashboard } from '@/features/admin/Dashboard';
import { AdminQueue } from '@/features/admin/Queue';
import { AdminRequests } from '@/features/admin/Requests';
import { AdminRequestDetail } from '@/features/admin/RequestDetail';
import { AdminEvaluations } from '@/features/admin/Evaluations';
import { AdminEvaluationDetail } from '@/features/admin/EvaluationDetail';
import { AdminViewings } from '@/features/admin/Viewings';
import { AdminViewingDetail } from '@/features/admin/ViewingDetail';
import { AdminUsers } from '@/features/admin/Users';
import { AdminUserDetail } from '@/features/admin/UserDetail';
import { AdminSignups } from '@/features/admin/Signups';
import { AdminMemberships } from '@/features/admin/Memberships';
import { AdminPayments } from '@/features/admin/Payments';
import { AdminAnalytics } from '@/features/admin/Analytics';
import { AdminSettings } from '@/features/admin/Settings';
import { BrokerDashboard } from '@/features/broker/Dashboard';
import { BrokerIncoming } from '@/features/broker/Incoming';
import { BrokerRequests } from '@/features/broker/Requests';
import { BrokerRequestDetail } from '@/features/broker/RequestDetail';
import { AddProperty } from '@/features/broker/AddProperty';
import { BrokerListings } from '@/features/broker/Listings';
import { BrokerViewings } from '@/features/broker/Viewings';
import { BrokerCommissions } from '@/features/broker/Commissions';
import { EvaluatorDashboard } from '@/features/evaluator/Dashboard';
import { EvaluatorIncoming } from '@/features/evaluator/Incoming';
import { EvaluatorRequests } from '@/features/evaluator/Requests';
import { EvaluatorRequestDetail } from '@/features/evaluator/RequestDetail';
import { MembershipPage } from '@/features/shared/MembershipPage';

/** Role gate. An admin may enter a broker/valuer area only while "acting as" one of them. */
function Guarded({ me, role, children }: { me: Me | null | undefined; role: 'admin' | 'broker' | 'evaluator'; children: React.ReactNode }) {
  const loc = useLocation();
  if (me === undefined) return <div className="p-10"><Skeleton lines={5} /></div>;
  if (!me) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (me.user.mustChangePassword) return <Navigate to="/login" replace />;
  const acting = actAs.get();
  if (me.user.role !== role && !(me.user.role === 'admin' && acting?.role === role)) return <Navigate to={`/${me.user.role === 'user' ? 'login' : me.user.role}`} replace />;
  return <>{children}</>;
}

const homeFor = (me: Me | null | undefined) => (me && me.user.role !== 'user' ? `/${me.user.role}` : '/login');

export function App() {
  const session = useSession();
  const me = session.isLoading ? undefined : (session.data ?? null);
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={me === undefined ? <div className="p-10"><Skeleton lines={5} /></div> : <Navigate to={homeFor(me)} replace />} />

      <Route path="/admin" element={<Guarded me={me} role="admin"><Shell me={me!} area="admin" /></Guarded>}>
        <Route index element={<AdminDashboard me={me!} />} />
        <Route path="queue" element={<AdminQueue />} />
        <Route path="broker-requests" element={<AdminRequests />} />
        <Route path="broker-requests/:id" element={<AdminRequestDetail />} />
        <Route path="evaluations" element={<AdminEvaluations />} />
        <Route path="evaluations/:id" element={<AdminEvaluationDetail />} />
        <Route path="viewings" element={<AdminViewings />} />
        <Route path="viewings/:id" element={<AdminViewingDetail />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetail />} />
        <Route path="signups" element={<AdminSignups />} />
        <Route path="memberships" element={<AdminMemberships />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="/broker" element={<Guarded me={me} role="broker"><Shell me={me!} area="broker" /></Guarded>}>
        <Route index element={<BrokerDashboard me={me!} />} />
        <Route path="incoming" element={<BrokerIncoming me={me!} />} />
        <Route path="requests" element={<BrokerRequests />} />
        <Route path="requests/:id" element={<BrokerRequestDetail />} />
        <Route path="add-property" element={<AddProperty />} />
        <Route path="listings" element={<BrokerListings />} />
        <Route path="viewings" element={<BrokerViewings />} />
        <Route path="commissions" element={<BrokerCommissions />} />
        <Route path="membership" element={<MembershipPage role="broker" me={me!} />} />
      </Route>

      <Route path="/evaluator" element={<Guarded me={me} role="evaluator"><Shell me={me!} area="evaluator" /></Guarded>}>
        <Route index element={<EvaluatorDashboard me={me!} />} />
        <Route path="incoming" element={<EvaluatorIncoming me={me!} />} />
        <Route path="requests" element={<EvaluatorRequests />} />
        <Route path="requests/:id" element={<EvaluatorRequestDetail />} />
        <Route path="membership" element={<MembershipPage role="evaluator" me={me!} />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
